import { useState } from 'react';
import { addEnquiry } from '../../../hooks/useEnquiriesDB';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6hug4o57726vudt8360', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        await addEnquiry({
          id: `enq-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          message: formData.message,
          source: 'Home Page',
          status: 'new',
          createdAt: Date.now(),
        });
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', address: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gradient-to-b from-[#0A1628] to-[#0D1F3C] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
        <div className="contact-streak contact-streak-1"></div>
        <div className="contact-streak contact-streak-2"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
            Get a <span className="text-[#FFD700]">Quote</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg">
            Contact us today for professional electrical services
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-5 sm:p-8 border border-[#FFD700]/20">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-5">Contact Information</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700]/20 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-phone-fill text-[#FFD700] text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Phone</h4>
                    <a href="tel:02031981818" className="text-gray-400 hover:text-[#FFD700] transition-colors text-sm block">020 3198 1818</a>
                    <a href="tel:+447989675952" className="text-gray-400 hover:text-[#FFD700] transition-colors text-sm block">+44 7989 675952</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700]/20 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-mail-fill text-[#FFD700] text-lg sm:text-xl"></i>
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Email</h4>
                    <a href="mailto:admin@beyondelectrical.co.uk" className="text-gray-400 hover:text-[#FFD700] transition-colors text-sm break-all">admin@beyondelectrical.co.uk</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700]/20 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-fill text-[#FFD700] text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Service Areas</h4>
                    <p className="text-gray-400 text-sm">Watford, Rickmansworth, Croxley, Pinner, Northwood, Bushey, Abbots Langley &amp; more</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700]/20 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-global-line text-[#FFD700] text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Website</h4>
                    <a href="http://www.beyondelectrical.co.uk/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FFD700] transition-colors text-sm">beyondelectrical.co.uk</a>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#FFD700]/20">
                <h4 className="text-white font-semibold mb-3 text-sm sm:text-base">Follow Us</h4>
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/Beyondelectricalservices/?locale=en_GB" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#FFD700]/20 rounded-lg flex items-center justify-center hover:bg-[#FFD700] transition-all duration-300 group cursor-pointer">
                    <i className="ri-facebook-fill text-[#FFD700] group-hover:text-[#0A1628] text-xl"></i>
                  </a>
                  <a href="https://www.instagram.com/beyond_electrical/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#FFD700]/20 rounded-lg flex items-center justify-center hover:bg-[#FFD700] transition-all duration-300 group cursor-pointer">
                    <i className="ri-instagram-fill text-[#FFD700] group-hover:text-[#0A1628] text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-5 sm:p-8 border border-[#FFD700]/20">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Certifications</h3>
              <div className="flex flex-wrap gap-3">
                {['NICEIC Approved', 'City & Guilds', 'BS7671 Certified'].map((cert) => (
                  <div key={cert} className="bg-white/10 px-3 py-1.5 rounded-lg">
                    <span className="text-[#FFD700] font-semibold text-xs sm:text-sm">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-5 sm:p-8 border border-[#FFD700]/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-5">Send us a message</h3>
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-1.5 text-sm">Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required maxLength={100}
                    className="w-full px-4 py-3 bg-white/10 border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-colors text-sm"
                    placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-1.5 text-sm">Email *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required maxLength={50}
                    className="w-full px-4 py-3 bg-white/10 border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-colors text-sm"
                    placeholder="your@email.com" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-white font-medium mb-1.5 text-sm">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required maxLength={30}
                    className="w-full px-4 py-3 bg-white/10 border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-colors text-sm"
                    placeholder="Your phone number" />
                </div>
                <div>
                  <label htmlFor="address" className="block text-white font-medium mb-1.5 text-sm">Job Address *</label>
                  <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required maxLength={100}
                    className="w-full px-4 py-3 bg-white/10 border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-colors text-sm"
                    placeholder="Full address including postcode" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-1.5 text-sm">Job Description *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required maxLength={500} rows={4}
                    className="w-full px-4 py-3 bg-white/10 border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-colors resize-none text-sm"
                    placeholder="Please describe the electrical work you need..."></textarea>
                  <p className="text-gray-500 text-xs mt-1">{formData.message.length}/500 characters</p>
                </div>
                <button type="submit" disabled={isSubmitting}
                  className="w-full bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-lg font-bold text-base hover:bg-[#FFC700] transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
                </button>
                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-300 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your message has been sent. We'll get back to you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
                    Sorry, there was an error. Please try again or call us directly.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes contact-streak {
          0% { transform: translateX(-100%) translateY(-50%) rotate(45deg); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateX(200%) translateY(150%) rotate(45deg); opacity: 0; }
        }
        .contact-streak {
          position: absolute; width: 2px; height: 250px;
          background: linear-gradient(to bottom, transparent, #FFD700, transparent);
          animation: contact-streak linear infinite;
        }
        .contact-streak-1 { left: 30%; top: 0; animation-duration: 6s; }
        .contact-streak-2 { left: 70%; top: 0; animation-duration: 7s; animation-delay: 3s; }
      `}</style>
    </section>
  );
}
